"use client";

import type { NormalizedCart } from "./types";

const GUEST_CART_HANDOFF_KEY = "guest-cart-handoff";

export type GuestCartHandoffItem = {
  variantId: number;
  quantity: number;
};

export type GuestCartHandoff = {
  items: GuestCartHandoffItem[];
  savedAt: number;
};

function isPositiveInteger(value: unknown): value is number {
  return Number.isInteger(value) && Number(value) > 0;
}

export function saveGuestCartHandoff(cart: NormalizedCart) {
  if (typeof window === "undefined" || cart.type !== "guest") {
    return;
  }

  const items = cart.items
    .map((item) => ({
      variantId: item.variantId,
      quantity: Math.max(1, item.quantity),
    }))
    .filter((item) => isPositiveInteger(item.variantId));

  try {
    if (items.length === 0) {
      window.localStorage.removeItem(GUEST_CART_HANDOFF_KEY);
      return;
    }

    window.localStorage.setItem(
      GUEST_CART_HANDOFF_KEY,
      JSON.stringify({ items, savedAt: Date.now() }),
    );
  } catch {
    // Login can still continue even when storage is unavailable.
  }
}

export function readGuestCartHandoff(): GuestCartHandoff | null {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    const rawValue = window.localStorage.getItem(GUEST_CART_HANDOFF_KEY);
    if (!rawValue) {
      return null;
    }

    const parsed = JSON.parse(rawValue) as Partial<GuestCartHandoff>;
    const items = Array.isArray(parsed.items)
      ? parsed.items
          .map((item) => ({
            variantId: Number(item.variantId),
            quantity: Math.max(1, Number(item.quantity) || 1),
          }))
          .filter((item) => isPositiveInteger(item.variantId))
      : [];

    return items.length > 0
      ? { items, savedAt: Number(parsed.savedAt) || Date.now() }
      : null;
  } catch {
    return null;
  }
}

export function clearGuestCartHandoff() {
  if (typeof window === "undefined") {
    return;
  }

  try {
    window.localStorage.removeItem(GUEST_CART_HANDOFF_KEY);
  } catch {
    // Nothing to clean up when storage is unavailable.
  }
}
